import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { programService } from 'src/config/rootService';

import { ProgramFragment, ProgramPageFragment } from '../documents/getProgram.generated';
import { NewPageTypes, PageConversion, PageType } from '../models/program';

interface ProgramContextValue {
    program: ProgramFragment;
    currentPage: ProgramPageFragment | null;
    currentPageNumber: number;
    canContinue: boolean;
    canSelectNextAnswer: boolean;
    isFirstPage: boolean;
    /**
     * Whether the user is on the last page of content.
     * `finished` indicates they are _past_ the last page
     * of content (e.g. on program ratings)
     */
    isLastPage: boolean;
    completionPercent: number;
    nextPage(): void;
    previousPage(): void;
    saveAnswers(): void;
    finish(): void;
    /**
     * Whether early exit feedback should be shown when
     * the user exits the content early.
     */
    showEarlyExitFeedback: boolean;
    /**
     * An exit handler.
     */
    onExit(): void;
}

export const getCanContinue = (page?: ProgramPageFragment) => {
    if (!page) {
        return true;
    }

    const numberOfSelectedAnswers =
        page.parsedAnswerOptions?.filter(answer => answer.isSelected).length ?? 1;

    if (page.type === PageType.QuestionMultiselect) {
        const minSelectable = page.parsedMultiselectConfig?.minSelectable ?? 1;
        const maxSelectable =
            page.parsedMultiselectConfig?.maxSelectable ?? page?.parsedAnswerOptions?.length ?? 1;

        return numberOfSelectedAnswers <= maxSelectable && numberOfSelectedAnswers >= minSelectable;
    }

    if (PageConversion[page.type] === NewPageTypes.SingleSelectQuestion) {
        return numberOfSelectedAnswers === 1;
    }

    return true;
};

export const getCanSelectNextAnswer = (page?: ProgramPageFragment) => {
    if (!page) {
        return false;
    }

    const numberOfSelectedAnswers =
        page.parsedAnswerOptions?.filter(answer => answer.isSelected).length ?? 1;
    const type = PageConversion[page.type];

    if (type === NewPageTypes.MultiSelectQuestion) {
        const maxSelectable =
            page.parsedMultiselectConfig?.maxSelectable ?? page?.parsedAnswerOptions?.length ?? 1;

        return numberOfSelectedAnswers < maxSelectable;
    }

    if (type === NewPageTypes.SingleSelectQuestion) {
        // we always allow to select next answer because we replace it in reducer
        return true;
    }

    return false;
};

export async function finishProgram(finishedProgram: ProgramFragment): Promise<void> {
    const lastPage = finishedProgram.pages[finishedProgram.pages.length - 1];

    if (!lastPage) {
        return;
    }

    const answerSelections =
        lastPage.parsedAnswerOptions
            ?.filter(answer => answer?.isSelected)
            .map(answer => ({
                optionValue: answer?.text ?? '',
                textValue: answer?.customOptionValue || '',
            })) ?? [];

    if (finishedProgram.name && lastPage.uuid) {
        programService.finishPage(lastPage.uuid, answerSelections).then(() => {
            if (finishedProgram.name) {
                return programService.finishProgram(finishedProgram.uuid);
            }

            return Promise.resolve(null);
        });
    }
}

export async function savePageAnswers(page?: ProgramPageFragment): Promise<void> {
    if (!page) {
        return;
    }

    const answerSelections =
        page.parsedAnswerOptions
            ?.filter(answer => answer?.isSelected)
            .map(answer => ({
                optionValue: answer?.text ?? '',
                textValue: answer?.customOptionValue || '',
            })) ?? [];

    if (page.uuid) {
        programService.finishPage(page.uuid, answerSelections);
    }
}

export const getCompletionPercent = (pageIndex, pageCount: number) => {
    return ((pageIndex + 1) / pageCount) * 100;
};

export const ProgramContext = createContext<ProgramContextValue | undefined>(undefined);

export function useProgramContext(): ProgramContextValue {
    const context = useContext(ProgramContext);

    if (!context) {
        throw new Error('ProgramContext used outside of ProgramWrapper.');
    }

    return context;
}

export const ProgramWrapper: React.FC<{
    program: ProgramFragment;
    showEarlyExitFeedback?: boolean;
    onExit: (finished: boolean) => void;
}> = ({ program, onExit, children, showEarlyExitFeedback = false }) => {
    // Only show if it is enabled (earlyExitFeedback) and the feature flag is targeting...

    const [pageNumber, setPageNumber] = useState(0);

    const page: ProgramPageFragment | null = useMemo(() => {
        return program.pages?.[pageNumber] ?? null;
    }, [program, pageNumber]);

    const completionPercent = useMemo(() => {
        return getCompletionPercent(pageNumber, program.pages.length);
    }, [program, pageNumber]);

    const canContinue = getCanContinue(page);
    const canSelectNextAnswer = getCanSelectNextAnswer(page);

    const isFirstPage = pageNumber === 0;
    const isLastPage = pageNumber === program.pages.length - 1;

    const finishProgramMutationRef = useRef(false);

    useEffect(() => {
        if (isLastPage && !finishProgramMutationRef.current) {
            finishProgramMutationRef.current = true;
            finishProgram(program);
        }
    }, [isLastPage]);

    const navigateToPage = (newPage: number) => {
        savePageAnswers(page);
        setPageNumber(newPage);
    };

    function nextPage(): void {
        const finished = pageNumber >= program.pages.length - 1;

        if (finished) {
            onExit(finished);
        } else {
            // If we're not finished, go to the next page.
            navigateToPage(pageNumber + 1);
        }
    }

    function onExitWrapper() {
        const finished = pageNumber >= program.pages.length - 1;

        onExit(finished);
    }

    function previousPage(): void {
        if (pageNumber > 0) {
            navigateToPage(pageNumber - 1);
        }
    }

    function finish(): void {
        finishProgram(program);
    }

    function saveAnswers(): void {
        if (page) {
            savePageAnswers(page);
        }
    }

    return (
        <ProgramContext.Provider
            value={{
                program,
                currentPage: page,
                currentPageNumber: pageNumber,
                completionPercent,
                canContinue,
                canSelectNextAnswer,
                saveAnswers,
                nextPage,
                previousPage,
                finish,
                isFirstPage,
                isLastPage,
                onExit: onExitWrapper,
                showEarlyExitFeedback,
            }}
        >
            {children}
        </ProgramContext.Provider>
    );
};
